<script setup lang="ts">
import { computed, ref } from 'vue';
import ImmediateChoice from './ImmediateChoice.vue';
export type PreparationOption = {
  apartment: string;
  offers: Array<{ offerId: string; displayName: string }>;
  availablePhysicalBoxes: Array<{ boxId: string; displayName: string }>;
};
const props = defineProps<{ options: PreparationOption[]; disabled?: boolean }>();
const emit = defineEmits<{ created: [] }>();
const apartment = ref('');
const offerId = ref('');
const boxId = ref('');
const busy = ref(false);
const error = ref('');
const selected = computed(() => props.options.find((o) => o.apartment === apartment.value));
function changeApartment(): void {
  offerId.value = '';
  boxId.value = '';
  error.value = '';
}
async function create(): Promise<void> {
  if (busy.value || props.disabled || !selected.value || !offerId.value || !boxId.value) return;
  busy.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch('/provider/api/preparations', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        apartmentName: apartment.value,
        selectedBoxOfferId: offerId.value,
        physicalBoxId: boxId.value,
      }),
    });
    if (!response.ok)
      throw new Error(
        'Préparation refusée. Actualisez : vérifiez le logement, l’offre et la disponibilité de la Box.',
      );
    boxId.value = '';
    emit('created');
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Préparation impossible.';
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <section class="manual-preparation" aria-labelledby="manual-preparation-title">
    <h2 id="manual-preparation-title">Préparer une Box hors commande</h2>
    <p>Choisissez son logement, son contenu et une Box physique disponible.</p>
    <form @submit.prevent="create">
      <ImmediateChoice
        id="manual-apartment"
        label="Logement"
        :model-value="apartment"
        :options="options.map((o) => ({ value: o.apartment, label: o.apartment }))"
        placeholder="Choisir un logement"
        :disabled="disabled || busy"
        @update:model-value="
          apartment = $event;
          changeApartment();
        "
      />
      <ImmediateChoice
        id="manual-offer"
        label="Offre à préparer"
        v-model="offerId"
        :options="
          (selected?.offers ?? []).map((o) => ({ value: o.offerId, label: o.displayName }))
        "
        placeholder="Choisir une offre"
        :disabled="disabled || busy || !selected"
      />
      <ImmediateChoice
        id="manual-box"
        label="Box physique disponible"
        v-model="boxId"
        :options="
          (selected?.availablePhysicalBoxes ?? []).map((o) => ({
            value: o.boxId,
            label: o.displayName,
          }))
        "
        placeholder="Choisir une Box"
        :disabled="disabled || busy || !selected"
      />
      <p v-if="selected && !selected.availablePhysicalBoxes.length" role="status">
        Aucune Box disponible pour ce logement. Récupérez une Box déjà livrée ou contactez
        l’administrateur.
      </p>
      <button
        class="button button-assign"
        type="submit"
        :disabled="disabled || busy || !offerId || !boxId"
      >
        {{ busy ? 'Création en cours…' : 'Commencer la préparation' }}
      </button>
      <p v-if="error" role="alert" class="flow-error">{{ error }}</p>
    </form>
  </section>
</template>
